import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Layout, FileText, MessageSquare, ArrowRight, User, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useEnhancedAnalytics } from '../hooks/useEnhancedAnalytics';
const WelcomeIntro = () => {
  const {
    setLanguage
  } = useLanguage();
  const {
    personProfile
  } = useEnhancedAnalytics();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userFirm, setUserFirm] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'vi' | null>(null);
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenIntro_v4');
    if (!hasSeenIntro) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleLanguageSelect = (lang: 'en' | 'vi') => {
    setSelectedLanguage(lang);
    setLanguage(lang);
    setCurrentStep(1);
  };
  const roleOptions = ['Recruiter', 'Hiring Manager', 'Investment Analyst', 'Portfolio Manager', 'Venture Capitalist', 'Private Equity Associate', 'Investment Banker', 'Trader', 'Student', 'Professor', 'Entrepreneur', 'Other'];
  const translations = {
    en: {
      languageTitle: 'Choose Your Language',
      languageDescription: 'Select your preferred language to continue',
      english: 'English',
      vietnamese: 'Tiếng Việt',
      steps: [{
        title: 'Welcome to My Portfolio',
        description: "Hi, I'm Trung! This interactive site showcases my journey in Finance, Data Science, and Entrepreneurship.",
        icon: <Sparkles className="w-12 h-12 text-yellow-400" data-id="element-1172" />,
        color: 'from-blue-600 to-cyan-500'
      }, {
        title: 'Explore My Journey',
        description: 'Navigate through my Experience, Projects, and Skills using the top navigation bar. Everything is organized for easy access.',
        icon: <Layout className="w-12 h-12 text-blue-400" data-id="element-1173" />,
        color: 'from-indigo-600 to-purple-500'
      }, {
        title: 'Stock Pitches & Press',
        description: "Don't miss my featured stock pitches and press mentions. I've included detailed analyses and articles.",
        icon: <FileText className="w-12 h-12 text-green-400" data-id="element-1174" />,
        color: 'from-emerald-600 to-teal-500'
      }, {
        title: 'Meet My AI Helper',
        description: 'Look for the sparkle button in the bottom right! You can ask my custom AI assistant anything about my background.',
        icon: <MessageSquare className="w-12 h-12 text-pink-400" data-id="element-1175" />,
        color: 'from-pink-600 to-rose-500'
      }, {
        title: "Let's Get Acquainted",
        description: "I'd love to know who's visiting! Please share a bit about yourself.",
        icon: <User className="w-12 h-12 text-violet-400" data-id="element-1176" />,
        color: 'from-violet-600 to-fuchsia-500',
        isInput: true,
        fields: ['name', 'role', 'firm']
      }],
      namePlaceholder: 'Your name *',
      roleLabel: 'Your role *',
      firmPlaceholder: 'Your firm/company (optional)',
      skip: 'Skip',
      next: 'Next',
      finish: 'Finish',
      thankYou: 'Thanks for visiting, {name}!',
      enjoyExploring: 'Enjoy exploring the portfolio.'
    },
    vi: {
      languageTitle: 'Chọn Ngôn Ngữ',
      languageDescription: 'Chọn ngôn ngữ bạn muốn sử dụng',
      english: 'English',
      vietnamese: 'Tiếng Việt',
      steps: [{
        title: 'Chào Mừng Đến Portfolio Của Tôi',
        description: 'Xin chào, tôi là Trung! Trang web tương tác này giới thiệu hành trình của tôi trong Tài chính, Khoa học Dữ liệu và Khởi nghiệp.',
        icon: <Sparkles className="w-12 h-12 text-yellow-400" data-id="element-1177" />,
        color: 'from-blue-600 to-cyan-500'
      }, {
        title: 'Khám Phá Hành Trình',
        description: 'Điều hướng qua Kinh nghiệm, Dự án và Kỹ năng của tôi bằng thanh điều hướng phía trên. Mọi thứ được tổ chức để dễ dàng truy cập.',
        icon: <Layout className="w-12 h-12 text-blue-400" data-id="element-1178" />,
        color: 'from-indigo-600 to-purple-500'
      }, {
        title: 'Phân Tích Cổ Phiếu & Báo Chí',
        description: 'Đừng bỏ lỡ các phân tích cổ phiếu nổi bật và đề cập trên báo chí của tôi. Tôi đã bao gồm các phân tích chi tiết và bài viết.',
        icon: <FileText className="w-12 h-12 text-green-400" data-id="element-1179" />,
        color: 'from-emerald-600 to-teal-500'
      }, {
        title: 'Gặp Trợ Lý AI Của Tôi',
        description: 'Tìm nút sparkle ở góc dưới bên phải! Bạn có thể hỏi trợ lý AI tùy chỉnh của tôi bất cứ điều gì về lý lịch của tôi.',
        icon: <MessageSquare className="w-12 h-12 text-pink-400" data-id="element-1180" />,
        color: 'from-pink-600 to-rose-500'
      }, {
        title: 'Hãy Làm Quen',
        description: 'Tôi muốn biết ai đang ghé thăm! Vui lòng chia sẻ một chút về bản thân bạn.',
        icon: <User className="w-12 h-12 text-violet-400" data-id="element-1181" />,
        color: 'from-violet-600 to-fuchsia-500',
        isInput: true,
        fields: ['name', 'role', 'firm']
      }],
      namePlaceholder: 'Tên của bạn *',
      roleLabel: 'Vai trò của bạn *',
      firmPlaceholder: 'Công ty của bạn (tùy chọn)',
      skip: 'Bỏ qua',
      next: 'Tiếp theo',
      finish: 'Hoàn thành',
      thankYou: 'Cảm ơn bạn đã ghé thăm, {name}!',
      enjoyExploring: 'Chúc bạn khám phá portfolio vui vẻ.'
    }
  };
  const t = selectedLanguage ? translations[selectedLanguage] : translations.en;
  const steps = selectedLanguage ? t.steps : [];
  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // SAVE USER DATA PROPERLY
      if (userName.trim() && userRole.trim()) {
        const userData = {
          name: userName.trim(),
          role: userRole.trim(),
          company: userFirm.trim() || 'N/A'
        };
        // Save to visitorData for compatibility
        localStorage.setItem('visitorData', JSON.stringify({
          ...userData,
          firm: userData.company,
          timestamp: new Date().toISOString()
        }));
        // CRITICAL: Update or CREATE person profile
        const existingPerson = localStorage.getItem('person_profile');
        if (existingPerson) {
          try {
            const person = JSON.parse(existingPerson);
            person.name = userData.name;
            person.role = userData.role;
            person.company = userData.company;
            localStorage.setItem('person_profile', JSON.stringify(person));
            console.log('[WelcomeIntro] ✅ Updated person profile:', userData);
          } catch (error) {
            console.error('[WelcomeIntro] Failed to update person profile:', error);
          }
        } else {
          // CREATE new person profile if doesn't exist
          const newPerson = {
            person_id: `person_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: userData.name,
            role: userData.role,
            company: userData.company,
            first_seen: new Date().toISOString(),
            last_seen: new Date().toISOString(),
            total_sessions: 1,
            total_time_spent: 0,
            consent_records: []
          };
          localStorage.setItem('person_profile', JSON.stringify(newPerson));
          console.log('[WelcomeIntro] ✅ Created new person profile:', userData);
        }
        setShowThankYou(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        handleClose();
      }
    }
  };
  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('hasSeenIntro_v4', 'true');
  };
  const handleSkip = () => {
    handleClose();
  };
  return <AnimatePresence data-id="element-1182">
      {isVisible && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-1183">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative border border-white/20" data-id="element-1184">
            <div className={`absolute inset-0 bg-gradient-to-br ${selectedLanguage && steps[currentStep] ? steps[currentStep].color : 'from-blue-600 to-cyan-500'} opacity-10 transition-colors duration-500`} data-id="element-1185" />

            <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors z-10" data-id="element-1186">
              <X size={20} data-id="element-1187" />
            </button>

            <div className="p-8 flex flex-col items-center text-center relative z-10" data-id="element-1188">
              {!selectedLanguage ? <>
                  <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full shadow-lg flex items-center justify-center mb-6 border-4 border-white dark:border-slate-700" data-id="element-1189">
                    <Globe className="w-12 h-12 text-blue-500" data-id="element-1190" />
                  </div>

                  <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white" data-id="element-1191">
                    {t.languageTitle}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-1192">
                    {t.languageDescription}
                  </p>

                  <div className="flex flex-col gap-3 w-full" data-id="element-1193">
                    <button onClick={() => handleLanguageSelect('en')} className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-lg shadow-blue-500/25 transition-all hover:scale-105 active:scale-95" data-id="element-1194">
                      {t.english}
                    </button>
                    <button onClick={() => handleLanguageSelect('vi')} className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold shadow-lg shadow-emerald-500/25 transition-all hover:scale-105 active:scale-95" data-id="element-1195">
                      {t.vietnamese}
                    </button>
                  </div>
                </> : showThankYou ? <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="py-10" data-id="element-1196">
                  <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 mx-auto" data-id="element-1197">
                    <Sparkles className="w-12 h-12 text-green-500" data-id="element-1198" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white" data-id="element-1199">
                    {t.thankYou.replace('{name}', userName)}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1200">
                    {t.enjoyExploring}
                  </p>
                </motion.div> : <>
                  <motion.div key={currentStep} initial={{
              scale: 0.5,
              rotate: -10,
              opacity: 0
            }} animate={{
              scale: 1,
              rotate: 0,
              opacity: 1
            }} transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20
            }} className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full shadow-lg flex items-center justify-center mb-6 border-4 border-white dark:border-slate-700" data-id="element-1201">
                    {steps[currentStep].icon}
                  </motion.div>

                  <motion.div key={`text-${currentStep}`} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1
            }} className="w-full" data-id="element-1202">
                    <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white" data-id="element-1203">
                      {steps[currentStep].title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed" data-id="element-1204">
                      {steps[currentStep].description}
                    </p>

                    {steps[currentStep].isInput && <div className="mb-6 space-y-3 text-left" data-id="element-1205">
                        <input type="text" value={userName} onChange={e => setUserName(e.target.value)} placeholder={t.namePlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all" autoFocus data-id="element-1206" />

                        <div data-id="element-1207">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1208">
                            {t.roleLabel}
                          </label>
                          <select value={userRole} onChange={e => setUserRole(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer" data-id="element-1209">
                            <option value="" data-id="element-1210">Select your role...</option>
                            {roleOptions.map(role => <option key={role} value={role} data-id="element-1211">
                                {role}
                              </option>)}
                          </select>
                        </div>

                        <input type="text" value={userFirm} onChange={e => setUserFirm(e.target.value)} placeholder={t.firmPlaceholder} className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all" onKeyDown={e => {
                  if (e.key === 'Enter' && userName.trim() && userRole.trim()) handleNext();
                }} data-id="element-1212" />
                      </div>}
                  </motion.div>

                  <div className="flex gap-2 mb-8" data-id="element-1213">
                    {steps.map((_, idx) => <div key={idx} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentStep ? 'w-6 bg-blue-600 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-700'}`} data-id="element-1214" />)}
                  </div>

                  <div className="flex gap-3 w-full" data-id="element-1215">
                    <button onClick={handleSkip} className="flex-1 py-3 px-4 rounded-xl text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors" data-id="element-1216">
                      {t.skip}
                    </button>
                    <button onClick={handleNext} disabled={steps[currentStep].isInput && (!userName.trim() || !userRole.trim())} className={`flex-1 py-3 px-4 rounded-xl text-white font-medium shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 bg-gradient-to-r ${steps[currentStep].color} ${steps[currentStep].isInput && (!userName.trim() || !userRole.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1217">
                      {currentStep === steps.length - 1 ? t.finish : t.next}
                      <ArrowRight size={18} data-id="element-1218" />
                    </button>
                  </div>
                </>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};
export default WelcomeIntro;